import { computed, inject, Injectable } from '@angular/core';
import { ActivityStreamItemType, CONFIG_TOKEN, Feature, NavigationItemGroup } from '@pxl/models/common';
import { CommonService } from '@pxl/models/contracts';
import { ActivityStreamItemModel } from '@pxl/shared/generated';
import { flatten } from 'lodash-es';

const featureTLEntryMap = {
    [Feature.plans]: [
        ActivityStreamItemType.TreatmentPlanNew,
        ActivityStreamItemType.TreatmentPlanUpdated,
        ActivityStreamItemType.TreatmentPlanCompleted,
        ActivityStreamItemType.TreatmentReminder,
        ActivityStreamItemType.TreatmentPlanStatusUpdated,
        ActivityStreamItemType.VaccinationPlanNew,
        ActivityStreamItemType.VaccinationPlanUpdated,
        ActivityStreamItemType.VaccinationReminder,
        ActivityStreamItemType.VaccinationPlanNeedsApproval,
        ActivityStreamItemType.VaccinationPlanStatusUpdated,
        ActivityStreamItemType.TherapyPlanNew,
        ActivityStreamItemType.TherapyReminder,
        ActivityStreamItemType.TherapyPlanCompleted,
    ],
    [Feature.emergency]: [] as string[],
    [Feature.DocumentsFromVeterinarian]: [
        ActivityStreamItemType.DocumentsFromVeterinarianNew,
        ActivityStreamItemType.DocumentsFromVeterinarianReminder,
    ],
    [Feature.VeterinarianLinkageRequest]: [] as string[],
    [Feature.OnlineCheckIn]: [] as string[],
    [Feature.FeedingRecommendation]: [] as string[],
    [Feature.SystemMessages]: ActivityStreamItemType.systemMessageEntries,
    [Feature.InAppReview]: [] as string[],
    [Feature.PatientInsurance]: [] as string[],
    [Feature.Chatbot]: [] as string[],
    [Feature.UnregisteredDataHealthRecordPackage]: [] as string[],
    [Feature.MultiFactorAuthentication]: [] as string[],
    [Feature.InsuranceClaimSubmission]: [] as string[],
    [Feature.InsuranceOffer]: [ActivityStreamItemType.InsuranceOfferRequested],
};

function isFeature(key: string): key is Feature {
    return Object.keys(Feature).includes(key);
}

@Injectable()
export class FeatureService {
    private readonly config = inject(CONFIG_TOKEN);
    private readonly $beDisabledFeatures = inject(CommonService).$disabledFeatures;
    private readonly $hiddenFeatures = computed(() => [...(this.config.hiddenFeatures ?? []), ...this.$beDisabledFeatures()]);

    public readonly $hiddenActivityStreamItemTypes = computed(() => {
        const hiddenFeatures = this.$hiddenFeatures();
        if (!hiddenFeatures) return [];
        const hiddenFeatureLists = hiddenFeatures.filter(isFeature).map((key) => featureTLEntryMap[key] ?? []);
        return flatten(hiddenFeatureLists);
    });

    public isFeatureHidden(feature: Feature): boolean {
        return this.$hiddenFeatures().includes(feature);
    }

    public filterTimelineEntriesByHiddenFeatures(entries: ActivityStreamItemModel[]): ActivityStreamItemModel[] {
        return entries.filter((entry) => !this.$hiddenActivityStreamItemTypes().includes(entry.activityStreamItemTypeId));
    }

    public replaceHiddenEntriesWithDummy(entries: ActivityStreamItemModel[]) {
        return entries.map((entry) => {
            if (!this.isHiddenActivityStreamItem(entry)) return entry;
            const newEntry: ActivityStreamItemModel = { ...entry };
            newEntry.activityStreamItemTypeId = ActivityStreamItemType.DisabledFeature;
            newEntry.veterinarianId = null;
            return newEntry;
        });
    }

    public filterNavigationItemGroups<T extends NavigationItemGroup>(menuGroups: T[]): T[] {
        if (!this.$hiddenActivityStreamItemTypes().length) return menuGroups;
        return menuGroups.map((group) => ({
            ...group,
            navigationItems: group.navigationItems.filter((item) => !item.feature || !this.isFeatureHidden(item.feature)),
        }));
    }

    private isHiddenActivityStreamItem(entry: ActivityStreamItemModel) {
        return this.$hiddenActivityStreamItemTypes().includes(entry.activityStreamItemTypeId);
    }
}
